<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import UIKit from '@pocketprep/ui-kit'
import { tutorModule } from '@/store/tutor/module'
import { analyticsModule } from '@/store/analytics/module'
import type { TQuizMode } from '@/store/quiz/state'
import { getNumericQuizMode } from '@/utils'

const { Icon } = UIKit

type TTutorNudge = 'eya' | 'stumped' | 'incorrectAnswer'

const props = withDefaults(
    defineProps<{
        currentQuestion?: Study.Class.QuestionJSON | null
        canShowTutorChat?: boolean
        isCurrentQuestionComplete?: boolean
        levelUpLearningTagQuestion?: boolean
        currentAnswerChecked?: boolean
        currentAnswerIsCorrect?: boolean
        hasSelectedChoice?: boolean
        isExplanationOpen?: boolean
        currentExam?: Study.Class.ExamMetadataJSON | ClientExam | null
        quizMode?: TQuizMode | null
    }>(),
    {
        currentQuestion: null,
        canShowTutorChat: false,
        isCurrentQuestionComplete: false,
        levelUpLearningTagQuestion: false,
        currentAnswerChecked: false,
        currentAnswerIsCorrect: false,
        hasSelectedChoice: false,
        isExplanationOpen: false,
        currentExam: null,
        quizMode: null,
    }
)

const emit = defineEmits<{
    openTutor: []
}>()

const tutorNudgeType = ref<TTutorNudge | null>(null)
const showTutorNudge = ref(false)
const explanationHasOpened = ref(false)
const hasViewedExplanationForTenSec = ref(false)
const hasBeenOnQuestionForThreeMin = ref(false)
const explanationTenSecondTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const tutorNudgeAutoCloseTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const questionThreeMinuteTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const getNudgeQuestionCount = () => {
    const count = Number(localStorage.getItem('nudgeQuestionCount'))
    return Number.isFinite(count) ? count : 0
}

const isAITutorChatOpen = computed(() => {
    return tutorModule.state.isChatOpen
})

const chatCost = computed(() => {
    return tutorModule.state.cost
})

const eyaRequirementsMet = computed(() => {
    if (!props.canShowTutorChat || !props.levelUpLearningTagQuestion) {
        return false
    }

    // question has been fully answered but not checked
    return !props.currentAnswerChecked && props.isCurrentQuestionComplete
})

const incorrectAnswerNudgeRequirementsMet = computed(() => {
    if (!props.canShowTutorChat || props.levelUpLearningTagQuestion) {
        return false
    }

    // only show for questions answered incorrectly
    if (props.currentAnswerIsCorrect && props.currentAnswerChecked) {
        return false
    }

    // do not show if nudge has already been shown for this question
    const hasSeenNudgeForCurrentQuestion = hasShownNudgeForQuestionId()
    if (hasSeenNudgeForCurrentQuestion) {
        return false
    }

    // only show if the explanation is open
    if (!explanationHasOpened.value) {
        return false
    }

    // only show if the user has viewed the explanation for at least 10 seconds
    if (!hasViewedExplanationForTenSec.value) {
        return false
    }

    // only show after user has attempted 20 questions since last nudge
    const nudgeQuestionSeenCount = getNudgeQuestionCount()
    if (nudgeQuestionSeenCount && nudgeQuestionSeenCount < 20) {
        return false
    }

    return (!props.currentAnswerIsCorrect && props.currentAnswerChecked)
})

const stumpedNudgeRequirementsMet = computed(() => {
    if (!props.canShowTutorChat) {
        return false
    }

    // only show if the user has not selected any choices
    if (props.hasSelectedChoice) {
        return false
    }

    // only show if the user has been on the question for at least 3 minutes
    if (!hasBeenOnQuestionForThreeMin.value) {
        return false
    }

    // do not show if nudge has already been shown for this question
    const hasSeenNudgeForCurrentQuestion = hasShownNudgeForQuestionId()
    if (hasSeenNudgeForCurrentQuestion) {
        return false
    }

    // only show after user has attempted 20 questions since last nudge
    const nudgeQuestionSeenCount = getNudgeQuestionCount()
    return !nudgeQuestionSeenCount || (nudgeQuestionSeenCount && nudgeQuestionSeenCount >= 20)
})

const nudgeMessage = computed(() => {
    if (tutorNudgeType.value === 'eya') {
        return 'Why do you think this is the answer?'
    }

    if (tutorNudgeType.value === 'incorrectAnswer') {
        return 'Do you understand why this is the correct answer?'
    }

    if (tutorNudgeType.value === 'stumped') {
        return 'Want some help breaking this question down?'
    }

    return null
})

onBeforeUnmount(() => {
    clearAllTimers()
})

const hasShownNudgeForQuestionId = () => {
    const shownForQuestionIds: string[] = JSON.parse(localStorage.getItem('tutorNudgesShownForQuestionIds') || '[]')
    return props.currentQuestion?.objectId && shownForQuestionIds.includes(props.currentQuestion?.objectId)
}

const updateTutorNudgeTracking = () => {
    if (!props.currentQuestion?.objectId) {
        return
    }

    const existing = localStorage.getItem('tutorNudgesShownForQuestionIds')
    const questionIds: string[] = existing ? JSON.parse(existing) : []

    if (!questionIds.includes(props.currentQuestion?.objectId)) {
        questionIds.push(props.currentQuestion?.objectId)
        localStorage.setItem('tutorNudgesShownForQuestionIds', JSON.stringify(questionIds))
    }
}

const startQuestionThreeMinuteTimer = () => {
    hasBeenOnQuestionForThreeMin.value = false
    clearQuestionThreeMinuteTimeout()
    questionThreeMinuteTimeout.value = setTimeout(() => {
        hasBeenOnQuestionForThreeMin.value = true
        clearQuestionThreeMinuteTimeout()
    }, 180000)
}

const clearExplanationTenSecondTimeout = () => {
    if (explanationTenSecondTimeout.value) {
        clearTimeout(explanationTenSecondTimeout.value)
        explanationTenSecondTimeout.value = null
    }
    return
}
const clearTutorNudgeAutoCloseTimeout = () => {
    if (tutorNudgeAutoCloseTimeout.value) {
        clearTimeout(tutorNudgeAutoCloseTimeout.value)
        tutorNudgeAutoCloseTimeout.value = null
    }
}

const clearQuestionThreeMinuteTimeout = () => {
    if (questionThreeMinuteTimeout.value) {
        clearTimeout(questionThreeMinuteTimeout.value)
        questionThreeMinuteTimeout.value = null
    }
    return
}

const clearAllTimers = () => {
    clearTutorNudgeAutoCloseTimeout()
    clearExplanationTenSecondTimeout()
    clearQuestionThreeMinuteTimeout()
}

const openTutor = () => {
    showTutorNudge.value = false
    tutorNudgeType.value = null
    clearAllTimers()
    emit('openTutor')
}

const closeNudge = () => {
    showTutorNudge.value = false
    tutorNudgeType.value = null
    tutorModule.state.nudgePrimingMessageType = null
    clearAllTimers()
}

const incrementNudgeQuestionCount = () => {
    const currentCount = Number.parseInt(localStorage.getItem('nudgeQuestionCount') || '0', 10)
    const safeCount = Number.isFinite(currentCount) ? currentCount : 0
    localStorage.setItem('nudgeQuestionCount', String(safeCount + 1))
}

const nudgeShownAmplitudeEvent = () => {
    const chatUuid = tutorModule.getters.getChatUuid(props.currentQuestion?.serial, props.currentExam?.objectId)
    analyticsModule.actions.amplitudeTrack('Tutor Nudge Shown', {
        chatUuid: chatUuid || '',
        cost: chatCost.value,
        examMetadataId: props.currentExam?.objectId || undefined,
        examName: props.currentExam?.nativeAppName || '',
        quizMode: props.quizMode ? Number(getNumericQuizMode(props.quizMode)) : 10000,
        serial: props.currentQuestion?.serial || '',
        nudgeType: tutorNudgeType.value === 'eya'
            ? 'Explain Your Answer'
            : tutorNudgeType.value === 'incorrectAnswer'
                ? 'Incorrect Answer'
                : 'Stumped',
    })
}

watch(eyaRequirementsMet, () => {
    if (eyaRequirementsMet.value) {
        if (!isAITutorChatOpen.value) {
            const hasSeenNudgeForCurrentQuestion = hasShownNudgeForQuestionId()
            const nudgeQuestionSeenCount = getNudgeQuestionCount()
            // show if a nudge has not already been shown for this question
            // And show after user has attempted 10 questions since last nudge
            if (!hasSeenNudgeForCurrentQuestion && nudgeQuestionSeenCount && nudgeQuestionSeenCount >= 10) {
                tutorNudgeType.value = 'eya'
                showTutorNudge.value = true
                updateTutorNudgeTracking()
                localStorage.setItem('nudgeQuestionCount', '0')
            }
        }
        // We want to show the eya priming message every time the eya requirements are met
        // regardless if we show the nudge or not
        tutorModule.state.nudgePrimingMessageType = 'eya'
    }
}, { immediate: true })

watch(incorrectAnswerNudgeRequirementsMet, () => {
    if (incorrectAnswerNudgeRequirementsMet.value) {
        // if chat is closed show nudge
        // if chat is open do not show nudge and stream in priming message
        if (!isAITutorChatOpen.value) {
            tutorNudgeType.value = 'incorrectAnswer'
            showTutorNudge.value = true
        }
        tutorModule.state.nudgePrimingMessageType = 'incorrectAnswer'
        clearExplanationTenSecondTimeout()
        updateTutorNudgeTracking()
        localStorage.setItem('nudgeQuestionCount', '0')
    }
}, { immediate: true })

watch(stumpedNudgeRequirementsMet, () => {
    if (stumpedNudgeRequirementsMet.value) {
        // if chat is closed show nudge
        // if chat is open do not show nudge and stream in priming message
        if (!isAITutorChatOpen.value) {
            tutorNudgeType.value = 'stumped'
            showTutorNudge.value = true
        }
        tutorModule.state.nudgePrimingMessageType = 'stumped'
        updateTutorNudgeTracking()
        clearQuestionThreeMinuteTimeout()
        localStorage.setItem('nudgeQuestionCount', '0')
    }
}, { immediate: true })

watch(showTutorNudge, () => {
    if (showTutorNudge.value) {
        clearTutorNudgeAutoCloseTimeout()
        tutorNudgeAutoCloseTimeout.value = setTimeout(() => {
            showTutorNudge.value = false
            tutorNudgeType.value = null
            tutorModule.state.nudgePrimingMessageType = null
            hasBeenOnQuestionForThreeMin.value = false
            hasViewedExplanationForTenSec.value = false
            clearTutorNudgeAutoCloseTimeout()
        }, 10000)

        nudgeShownAmplitudeEvent()
        return
    }
    clearTutorNudgeAutoCloseTimeout()
}, { immediate: true })

watch(() => props.hasSelectedChoice, () => {
    if (props.canShowTutorChat && props.hasSelectedChoice) {
        clearQuestionThreeMinuteTimeout()
    }
})

watch(isAITutorChatOpen, () => {
    if (isAITutorChatOpen.value && showTutorNudge.value) {
        showTutorNudge.value = false
        tutorNudgeType.value = null
    }
    clearAllTimers()
})

watch(() => props.currentQuestion?.objectId, () => {
    const hasSeenNudgeForCurrentQuestion = hasShownNudgeForQuestionId()

    tutorNudgeType.value = null
    showTutorNudge.value = false
    explanationHasOpened.value = false
    tutorModule.state.nudgePrimingMessageType = null
    hasBeenOnQuestionForThreeMin.value = false
    hasViewedExplanationForTenSec.value = false
    clearAllTimers()

    if (props.canShowTutorChat) {
        startQuestionThreeMinuteTimer()
        if (!hasSeenNudgeForCurrentQuestion) {
            incrementNudgeQuestionCount()
        }
    }
}, { immediate: true })

watch(() => props.isExplanationOpen, (isOpen) => {
    if (isOpen) {
        explanationHasOpened.value = true
        if (
            props.canShowTutorChat
            && !hasViewedExplanationForTenSec.value
            && !explanationTenSecondTimeout.value
        ) {
            explanationTenSecondTimeout.value = setTimeout(() => {
                hasViewedExplanationForTenSec.value = true
                clearExplanationTenSecondTimeout()
            }, 10000)
        }
    } else {
        clearExplanationTenSecondTimeout()
    }
}, { immediate: true })
</script>

<template>
    <div
        v-if="showTutorNudge && nudgeMessage && !isAITutorChatOpen"
        class="tutor-nudge"
        role="dialog"
        :aria-label="`${nudgeMessage}`"
        tabindex="0"
        @click="openTutor"
        @keydown.enter.prevent="openTutor"
        @keydown.space.prevent="openTutor"
    >
        <div
            v-dark
            class="tutor-nudge__bubble"
        >
            <Icon
                v-dark
                class="tutor-nudge__icon"
                type="tutorChat"
            />
            <div
                class="tutor-nudge__text"
                v-dark
            >
                {{ nudgeMessage }}
            </div>
            <Icon
                v-dark
                type="close"
                class="tutor-nudge__close"
                title="Close"
                tabindex="0"
                @click.stop="closeNudge"
                @keydown.enter.stop.prevent="closeNudge"
                @keydown.space.stop.prevent="closeNudge"
                @mousedown.stop.prevent
            />
        </div>
        <div
            v-dark
            class="tutor-nudge__tail"
            aria-hidden="true"
        />
    </div>
</template>

<style lang="scss" scoped>
.tutor-nudge {
    position: relative;
    display: flex;
    width: 268px;
    padding: 0;
    align-items: flex-start;
    cursor: pointer;

    &:focus {
        outline: 2px solid $cheddar;
        border-radius: 6px;
    }

    @include breakpoint(black-bear) {
        width: 100%;
    }

    &__bubble {
        display: flex;
        align-items: flex-start;
        padding: 10px 7px 10px 10px;
        border-radius: 6px;
        background: $evening;

        @include breakpoint(black-bear) {
            width: 100%;
        }

        &--dark {
            background: $fog;
        }
    }

    &__icon {
        width: 20px;
        height: 20px;
        margin-right: 7px;
        color: $banana-bread;

        &--dark {
            color: $brand-black;
        }
    }

    &__text {
        flex: 1;
        color: $white;
        font-size: 14px;
        font-weight: 600;
        line-height: 19px;
        margin-right: 16px;

        @include breakpoint(black-bear) {
            margin-right: 30px;
        }

        &--dark {
            color: $brand-black;
        }
    }

    &__close {
        height: 20px;
        width: 20px;
        color: $cloudy;

        @include breakpoint(black-bear) {
            position: absolute;
            right: 10px;
        }

        &:hover {
            color: $white;
        }

        &--dark {
            color: $steel;

            &:hover {
                color: $brand-black;
            }
        }
    }

    &__tail {
        position: absolute;
        right: 16px;
        top: 100%;
        width: 0;
        height: 0;
        border-left: 14px solid transparent;
        border-top: 8px solid $evening;

        @include breakpoint(black-bear) {
            display: none;
        }

        &--dark {
            border-top: 8px solid $fog;
        }
    }
}
</style>
